import type { ApiErrorObject, ErrorDocument } from './types'

/**
 * The single message shown for any credential failure.
 *
 * The API deliberately distinguishes "no such account" (404 record_not_found)
 * from "wrong password" (401 access_denied). Surfacing that difference in the
 * UI would turn the login form into an account-enumeration oracle: an attacker
 * could confirm which email addresses have Codeverse accounts — which, given
 * the users are children, is a genuine safety problem and not just a security
 * checkbox. Both codes therefore collapse to this one string.
 */
export const GENERIC_CREDENTIALS_MESSAGE =
  'That username, email, or password is not correct. Please try again.'

const UNAVAILABLE_MESSAGE = 'Something went wrong on our end. Please try again in a moment.'
const OFFLINE_MESSAGE = "We couldn't reach Codeverse. Check your connection and try again."
const INVALID_MESSAGE = 'Please check the details you entered and try again.'
const WEAK_PASSWORD_MESSAGE = 'Choose a stronger password.'
const BILLING_UNAVAILABLE_MESSAGE =
  'Our billing system is temporarily unavailable. Please try again in a moment.'
const BILLING_CHANGED_MESSAGE =
  'That couldn’t be done. The subscription may have changed, so check the details above and try again.'

const PASSWORD_POINTER = '/data/attributes/password'

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly detail: string
  readonly pointer: string | undefined
  readonly errors: ApiErrorObject[]

  constructor(status: number, errors: ApiErrorObject[]) {
    const first = errors[0]
    super(first?.detail ?? first?.title ?? `Request failed with status ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.code = first?.code ?? 'unknown_error'
    this.detail = first?.detail ?? ''
    this.pointer = first?.source?.pointer
    this.errors = errors
  }
}

/** Thrown when the request never reached the API (offline, DNS, CORS preflight). */
export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super('Could not reach the Codeverse API')
    this.name = 'NetworkError'
    this.cause = cause
  }
}

export function isErrorDocument(value: unknown): value is ErrorDocument {
  return (
    typeof value === 'object' &&
    value !== null &&
    Array.isArray((value as { errors?: unknown }).errors)
  )
}

/**
 * True when the failure means "these credentials don't work" — used by the
 * login form to decide whether to keep the password field populated.
 */
export function isCredentialError(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 401 || error.status === 404)
}

/**
 * Map any thrown value to something safe to show a user.
 *
 * Note that `error.detail` from the API is never shown directly. It is written
 * for developers, and for 401/404 it must not be distinguishable anyway.
 */
export function messageForError(error: unknown): string {
  if (error instanceof NetworkError) return OFFLINE_MESSAGE

  if (error instanceof ApiError) {
    if (isCredentialError(error)) return GENERIC_CREDENTIALS_MESSAGE

    switch (error.status) {
      case 400:
      case 422:
        // The one validation a user can act on directly: the API's password
        // strength check. Its detail is Rails prose, so the pointer is the signal.
        return error.pointer === PASSWORD_POINTER ? WEAK_PASSWORD_MESSAGE : INVALID_MESSAGE
      // 409 (wrong data.type) and 415 (wrong Accept header) are our bugs, not
      // the user's. Say something honest but not alarming, and rely on the
      // console error for diagnosis.
      case 409:
      case 415:
        return UNAVAILABLE_MESSAGE
      default:
        return UNAVAILABLE_MESSAGE
    }
  }

  return UNAVAILABLE_MESSAGE
}

/**
 * Map a failure from a billing endpoint, where Recharge is the source of
 * truth behind the API. Besides the usual failures it has two of its own: it
 * could not be reached (503), and it refused or no longer has the record
 * (422, 404) — which, in a row that offered the change, means the
 * subscription has moved on since the page loaded. Neither is a credentials
 * problem, so both are named before `messageForError` could read a 404 as one.
 */
export function messageForBillingError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 503) return BILLING_UNAVAILABLE_MESSAGE
    if (error.status === 404 || error.status === 422) return BILLING_CHANGED_MESSAGE
  }
  return messageForError(error)
}

const ATTRIBUTE_POINTER = /^\/data\/attributes\/(\w+)$/

/**
 * What the API refused about each attribute, by attribute name, for a form to
 * show beside the field concerned. Recharge words these for people ("Invalid
 * zip code"), unlike the platform's own validations. Errors without a pointer
 * are not included; a caller falls back to a message for the whole form.
 */
export function fieldErrors(error: unknown): Record<string, string> {
  const fields: Record<string, string> = {}
  if (!(error instanceof ApiError)) return fields
  for (const item of error.errors) {
    const attribute = item.source?.pointer?.match(ATTRIBUTE_POINTER)?.[1]
    if (attribute && item.detail) fields[attribute] ??= item.detail
  }
  return fields
}
